import { SyntheticEvent, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useConditionalEffect } from 'react-delta';
import { Col, Form } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as yup from 'yup';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { generateQueryTimeRange, validEmailFormat } from 'common/functions';
import { HELP_ROUTE } from 'components/help/constants';
import { useIsNewSearch } from 'hooks/useIsNewSearch';
import { parseDateTime, validateTimeInput } from 'common/util/time';

import Link from 'components/common/Link/Link';
import { MultiSelectInput } from 'components/common/ReactSelect/MultiSelectInput';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import TimeOptions from 'components/common/TimeOptions/TimeOptions';
import { searchParamRecordState } from 'components/common/Managers/RouteStateManager.state';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import { AddJobData, ExportJobFormData, JOB_OUTPUT } from 'components/bulkExports/types';
import { BUCKET_NAME_REGEXP, TIME_OPTIONS } from 'components/bulkExports/consts';

/**
 * Bulk exports form component which handles inputs values and validate them.
 * @param {Object}     formData        Initial form inputs' data, which can be empty or filled with values when the 'clone' functionality is used.
 * @param {function}   onSubmit        Callback when the form is submitted.
 */

interface BulkExportsFormProps {
  formData: ExportJobFormData;
  onSubmit: (formData: AddJobData) => void;
  submitButtonEnabled: boolean;
}

const initialFormState: ExportJobFormData = {
  timeRangeStart: '',
  timeRangeEnd: '',
  query: '',
  label: '',
  s3Bucket: '',
  filenamePrefix: '',
  recipients: [],
};
type MultiSelectValidation = {
  isValid: boolean;
  errorMessage: string;
};

function BulkExportsForm({ onSubmit, formData, submitButtonEnabled }: BulkExportsFormProps): JSX.Element {
  const searchParams = useRecoilValue(searchParamRecordState);
  const isNewSearch = useIsNewSearch();
  const timezone = useRecoilValue(timezoneState);
  const [emailError, setEmailError] = useState('');
  const [formState, setFormState] = useState<ExportJobFormData>(initialFormState);

  const handleSubmit = (values: ExportJobFormData) => {
    if (emailError === '') {
      const { startTimeNs, endTimeNs } = generateQueryTimeRange(timezone, values.timeRangeStart, values.timeRangeEnd);
      onSubmit({
        ...values,
        timeRangeStart: startTimeNs,
        timeRangeEnd: endTimeNs,
        output: JOB_OUTPUT.JSON,
      });
    }
  };

  const validationSchema = yup.object({
    timeRangeEnd: yup
      .string()
      .required('Please enter time.')
      .test(
        'Time Validation',
        'Please enter a valid time.',
        function testTimeRangeEnd(this: yup.TestContext, timeRangeEnd?: string): boolean | yup.ValidationError {
          const parsedEndTime = parseDateTime(timeRangeEnd, null, null, timezone);
          const parsedStartTime = this.parent.timeRangeStart
            ? parseDateTime(this.parent.timeRangeStart, null, null, timezone)
            : null;

          if (!parsedEndTime) {
            return false;
          }

          if (parsedStartTime && parsedEndTime.moment.isSameOrBefore(parsedStartTime.moment)) {
            return this.createError({ message: 'End time must be after start time' });
          }

          return true;
        },
      ),
    timeRangeStart: yup
      .string()
      .required('Please enter time.')
      .test('Time Validation', 'Please enter a valid time.', (timeRangeStart?: string) => {
        if (!timeRangeStart) {
          return false;
        }
        return validateTimeInput(timeRangeStart);
      }),
    query: yup.string().required('Please enter a query.'),
    label: yup.string(),
    s3Bucket: yup
      .string()
      .required('Please enter a S3 Bucket Name.')
      .matches(BUCKET_NAME_REGEXP, 'Please enter a valid S3 Bucket Name.'),
    filenamePrefix: yup.string(),
    recipients: yup.array().of(yup.string()),
  });

  const { values, resetForm, touched, errors, setFieldValue, setFieldTouched, handleChange, submitForm } = useFormik({
    initialValues: formState,
    onSubmit: handleSubmit,
    validationSchema,
    enableReinitialize: true,
    validateOnChange: false,
  });

  useEffect(() => {
    resetForm();
    setFormState(formData);
  }, [formData, resetForm]);

  // value of filter in URL is authoritative, so update when it changes
  useConditionalEffect(() => {
    setFormState({
      ...formState,
      query: searchParams[K.URL_PARAM.FILTER],
      timeRangeEnd: searchParams[K.URL_PARAM.END_TIME],
      timeRangeStart: searchParams[K.URL_PARAM.START_TIME],
    });
  }, isNewSearch);

  const validateEmail = (email: string): MultiSelectValidation => {
    let errorMessage = '';
    let isValid = true;
    if (email !== '') {
      if (values.recipients.includes(email)) {
        errorMessage = 'Duplicate email';
        isValid = false;
      }
      if (!validEmailFormat(email)) {
        errorMessage = 'Please enter a valid email';
        isValid = false;
      }
    }
    setEmailError(errorMessage);
    return {
      isValid,
      errorMessage,
    };
  };

  const emailLabel = (
    <>
      Email Notification of Export Status
      <span className="BulkExports__Label--Italic">(optional)</span>
    </>
  );

  return (
    <Form validated={false} className="BulkExports__Form">
      <div className="row">
        <Col className="BulkExports__Col">
          <span className="BulkExports__SubTitle" />
          <div className="BulkExports__TimeOptions">
            <TimeOptions
              header="Export the last..."
              options={TIME_OPTIONS}
              clickHandler={(time: string) => {
                setFieldValue('timeRangeEnd', K.DEFAULT_SEARCH_END, true);
                setFieldValue('timeRangeStart', time, true);
              }}
            />
          </div>
        </Col>
        <Col className="BulkExports__Col BulkExports__TimeInput">
          <Form.Label>Or be more specific...</Form.Label>
          <div className="form-row">
            <div className="col">
              <Form.Group controlId="timeRangeStart">
                <Form.Control
                  placeholder="FROM"
                  type="text"
                  name="timeRangeStart"
                  value={values.timeRangeStart}
                  isInvalid={touched.timeRangeStart && !!errors.timeRangeStart}
                  onChange={(event: SyntheticEvent) => {
                    const currentTarget = event.currentTarget as HTMLInputElement;
                    setFieldValue('timeRangeStart', currentTarget.value, !!errors.timeRangeStart);
                    if (values.timeRangeEnd === '') {
                      setFieldValue('timeRangeEnd', K.DEFAULT_SEARCH_END, true);
                    }
                  }}
                  onBlur={() => setFieldTouched('timeRangeStart', true)}
                />
                <Form.Control.Feedback type="invalid">{errors.timeRangeStart}</Form.Control.Feedback>
              </Form.Group>
            </div>
            <div className="col">
              <Form.Group controlId="timeRangeEnd">
                <Form.Control
                  placeholder="TO"
                  type="text"
                  name="timeRangeEnd"
                  value={values.timeRangeEnd}
                  isInvalid={touched.timeRangeEnd && !!errors.timeRangeEnd}
                  onChange={(event: SyntheticEvent) => {
                    const currentTarget = event.currentTarget as HTMLInputElement;
                    setFieldValue('timeRangeEnd', currentTarget.value, !!errors.timeRangeEnd);
                  }}
                  onBlur={() => setFieldTouched('timeRangeEnd', true)}
                />
                <Form.Control.Feedback type="invalid">{errors.timeRangeEnd}</Form.Control.Feedback>
              </Form.Group>
            </div>
          </div>
          <div className="BulkExports__FormText">
            You can specify times in many formats, like 3h for 3 hours, Oct 11 11:45:00 AM, Monday, etc.&nbsp;
            <Link href={HELP_ROUTE.HELP_TIME_REFERENCE} className="link" openInNewTab>
              See more
            </Link>
          </div>
        </Col>
      </div>

      <Form.Group controlId="query">
        <Form.Label>Query</Form.Label>
        <Form.Control
          placeholder="httpReq='scalyr'"
          type="text"
          name="query"
          value={values.query}
          isInvalid={touched.query && !!errors.query}
          onChange={handleChange}
        />
        <Form.Control.Feedback type="invalid">{errors.query}</Form.Control.Feedback>
      </Form.Group>
      <Form.Group controlId="label">
        <Form.Label>
          Label
          <span className="BulkExports__Label--Italic">(optional)</span>
        </Form.Label>
        <Form.Control
          placeholder="HTTP requests for 'scalyr'"
          type="text"
          name="label"
          value={values.label}
          onChange={handleChange}
        />
      </Form.Group>
      <Form.Group controlId="s3Bucket">
        <Form.Label>
          S3 Bucket for File Export&nbsp;
          <span className="BulkExports__Label--Italic">
            (make sure the S3 bucket is set up first,&nbsp;
            <Link href={HELP_ROUTE.HELP_EXPORTS} className="link" openInNewTab>
              see docs
            </Link>
            )
          </span>
        </Form.Label>
        <Form.Control
          placeholder="bucket.container.com"
          type="text"
          name="s3Bucket"
          value={values.s3Bucket}
          isInvalid={touched.s3Bucket && !!errors.s3Bucket}
          onChange={handleChange}
        />
        <Form.Control.Feedback type="invalid">{errors.s3Bucket}</Form.Control.Feedback>
      </Form.Group>
      <Form.Group controlId="filenamePrefix">
        <Form.Label>
          Filename Prefix
          <span className="BulkExports__Label--Italic">(optional)</span>
        </Form.Label>
        <Form.Control
          placeholder="scalyrExport_20170522"
          type="text"
          name="filenamePrefix"
          value={values.filenamePrefix}
          onChange={handleChange}
        />
      </Form.Group>
      <MultiSelectInput
        label={emailLabel}
        labelClassName="form-label"
        currentValue={values.recipients.map((recipient) => ({ label: recipient, value: recipient }))}
        placeholder="Enter one or more emails"
        validate={(email: string): MultiSelectValidation => validateEmail(email)}
        idContainer={ELEMENT.bulkExportsPageEmailInput}
        onChangeHandler={(options) => {
          const emailValues = options.map((option) => option.value);
          setFieldValue('recipients', emailValues);
          setEmailError('');
        }}
      />
      <Button
        disabled={!submitButtonEnabled}
        variant={ButtonVariant.PRIMARY}
        onClick={submitForm}
        className="BulkExports__Button"
        type="button"
      >
        Start Export
      </Button>
    </Form>
  );
}

export default BulkExportsForm;
